import { collection, doc, WriteBatch } from 'firebase/firestore';
import { captureException as sentryCaptureException } from '@sentry/react';

import { db } from '../firebase-config';

export const handleDefaultText = (recipientRole) => {
  if (recipientRole === 'nurse') {
    return 'Automated Message: You can use this chat to message the practice nurse with any medical questions. Please note this is not a live chat and our team will respond to you as soon as they are able';
  }
  if (recipientRole === 'support') {
    return 'Automated Message: You can use this chat to message the Candor support team for technical support and help with your account';
  }
  if (recipientRole === 'pharmacist') {
    return 'Automated Message: You can use this chat to message this pharmacy with any questions relating to your treatment or your order';
  }
  if (recipientRole === 'doctor') {
    return 'Automated Message: You can use this chat to message your Candor medical practitioner. This chat will only be active while you and your practitioner decide on a treatment option. After this, medical questions can be asked in the practice nurse chat';
  }
  return '';
};

/**
 * @typedef {Object} ParticipantDetails
 * @property {string} id - The ID of the participant
 * @property {string} email - The email of the participant
 * @property {string} name - The name of the participant
 * @property {number} notifications - The number of notifications the participant has
 * @property {string} role - The role of the participant
 */

/**
 * Creates a new conversation between two participants
 * @param {Object} params
 * @param {ParticipantDetails} params.participantDetails1 - Details of the first participant
 * @param {ParticipantDetails} params.participantDetails2 - Details of the second participant
 * @param {WriteBatch} params.batch - The batch to use for the creation of the conversation
 * @returns {{error: string | null, data: WriteBatch | null}} - An object containing an error and data
 */
export const createConversation = ({ participantDetails1, participantDetails2, batch }) => {
  try {
    const date = Date.now();

    const payload = {
      participant1: {
        id: participantDetails1.id,
        email: participantDetails1.email,
        name: participantDetails1.name,
        notifications: participantDetails1.notifications,
        role: participantDetails1.role,
      },
      participant2: {
        id: participantDetails2.id,
        email: participantDetails2.email,
        name: participantDetails2.name,
        notifications: participantDetails2.notifications,
        role: participantDetails2.role,
      },
      reference: `${participantDetails1.id}:${participantDetails2.id}`,
      updated: date,
      online: !(participantDetails2.role === 'nurse' || participantDetails2.role === 'doctor'),
    };

    // 👀 Keep note of this. There was a race-condition bug here before where it would not create the auto message..
    const conversationId = `${participantDetails1.id}:${participantDetails2.id}`;

    const conversationRef = doc(db, 'conversations', conversationId);
    const messageRef = doc(collection(db, 'conversations', conversationId, 'messages'));

    batch.set(conversationRef, payload);
    batch.set(messageRef, {
      from: 'candor',
      read: date,
      sent: date,
      text: handleDefaultText(participantDetails2.role),
      metadata: {
        type: 'AUTOMATED_MESSAGE',
      },
    });

    // Temp hack, improve architecture later
    return { error: null, data: batch };
  } catch (error) {
    const sentryIssueId = sentryCaptureException(error, {
      level: 'error',
      extra: { issueIn: 'createConversation', participantDetails1, participantDetails2 },
    });

    return { error: `Error creating conversation: ${sentryIssueId}`, data: null };
  }
};
